/**
 * 極簡的 History API 路由。
 *
 * mockup 用 hash 路由（`#/panel/...`），因為它要能在 `file://` 下雙擊開啟。
 * 正式站台由 caddy 提供、§3.5 的 Caddyfile 已經有 `try_files {path} /index.html`，
 * 所以用真正的路徑——§7.4 的路由表寫的就是 `/work`、`/station/:id`、`/admin/accounts`。
 *
 * 刻意不引入 react-router：這裡只需要「目前路徑」與「跳轉」，40 行就夠了（§2.2）。
 */

import { useCallback, useEffect, useState } from 'react'

export function useRoute(): { path: string; parts: string[] } {
  const [path, setPath] = useState(() => window.location.pathname || '/')

  useEffect(() => {
    const on = () => setPath(window.location.pathname || '/')
    window.addEventListener('popstate', on)
    window.addEventListener('pv:navigate', on)
    return () => {
      window.removeEventListener('popstate', on)
      window.removeEventListener('pv:navigate', on)
    }
  }, [])

  return { path, parts: path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean) }
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (to === window.location.pathname) return
  if (opts.replace) window.history.replaceState(null, '', to)
  else window.history.pushState(null, '', to)
  window.dispatchEvent(new Event('pv:navigate'))
  window.scrollTo(0, 0)
}

export function useNavigate(): (to: string, opts?: { replace?: boolean }) => void {
  return useCallback((to, opts) => navigate(to, opts ?? {}), [])
}

/** 站內連結。攔下左鍵點擊改用 History API，其餘（新視窗、中鍵）維持瀏覽器行為。 */
export function Link({ to, children, className, ...rest }: {
  to: string
  children: React.ReactNode
  className?: string
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
        e.preventDefault()
        navigate(to)
      }}
      {...rest}
    >{children}</a>
  )
}
